import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center border uppercase font-medium w-fit whitespace-nowrap shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-bad/20 dark:aria-invalid:ring-bad/40 aria-invalid:border-bad transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border border-border bg-primary text-primary-foreground",
        secondary:
          "border border-border bg-secondary text-secondary-foreground",
        bad: "text-bad-foreground bg-bad",
        good: "text-good-foreground bg-good",
        caution: "text-caution-foreground bg-caution",
        outline: "text-foreground",
        callout:
          "border bg-brand text-brand-foreground rotate-2 -translate-y-1",
      },
      size: {
        sm: "px-1.5 py-0.5 text-[10px] [&>svg]:size-2.5",
        default: "px-2 py-0.5 text-xs [&>svg]:size-3",
        lg: "px-3 py-1 text-sm [&>svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Badge({
  className,
  variant,
  size,
  shouldRenderAsChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { shouldRenderAsChild?: boolean }) {
  const Comp = shouldRenderAsChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
